import { AlertTriangle, Check, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import type {
  OrganizationOnboardingStep,
  OrganizationOnboardingStepKey,
  OrganizationUsageSummary,
  OrganizationUsageSummaryOnboarding,
} from '../services/adminApi';

type ExpectedStep = {
  key: OrganizationOnboardingStepKey;
  label: string;
  countBearing: boolean;
};

const EXPECTED_STEPS: ExpectedStep[] = [
  { key: 'owner_logged_in', label: 'Owner logged in', countBearing: false },
  { key: 'rubric_created', label: 'Rubric created', countBearing: false },
  { key: 'calls_uploaded', label: 'Calls uploaded', countBearing: true },
  { key: 'calls_scored', label: 'Calls scored', countBearing: true },
];

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US');

export type NormalizedOnboardingStep = {
  key: OrganizationOnboardingStepKey;
  label: string;
  position: number;
  completed: boolean;
  completedAt: string | null;
  count: number | null;
};

export function normalizeOnboardingSteps(
  onboarding: OrganizationUsageSummaryOnboarding | null | undefined
): NormalizedOnboardingStep[] {
  const byKey = new Map<string, OrganizationOnboardingStep>();
  for (const step of onboarding?.steps ?? []) {
    byKey.set(step.key, step);
  }

  return EXPECTED_STEPS.map((expected, index) => {
    const raw = byKey.get(expected.key);
    return {
      key: expected.key,
      label: expected.label,
      position: index + 1,
      completed: raw?.completed === true,
      completedAt: raw?.completed === true ? safeIso(raw.completedAt) : null,
      count: expected.countBearing ? toSafeNonNegativeInteger(raw?.count) ?? 0 : null,
    };
  });
}

export function completedStepCount(steps: NormalizedOnboardingStep[]): number {
  return steps.filter((step) => step.completed).length;
}

export function formatUsageMetric(
  value: number | null | undefined,
  emptyLabel: string
): string {
  const safeValue = toSafeNonNegativeInteger(value);
  return safeValue === null ? emptyLabel : NUMBER_FORMATTER.format(safeValue);
}

function toSafeNonNegativeInteger(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.max(0, Math.trunc(value));
}

function safeIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? value : null;
}

function formatTimestamp(value: string | null | undefined): string | null {
  const iso = safeIso(value);
  if (!iso) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatLastRefreshed(value: Date | null): string | null {
  if (!value) return null;
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(value);
}

function InlineCardError({ message }: { message: string }) {
  return (
    <div className="onboarding-usage-error" role="alert">
      <AlertTriangle className="h-5 w-5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

interface OnboardingUsageCardProps {
  summary: OrganizationUsageSummary | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastRefreshedAt: Date | null;
  onRefresh: () => void;
}

export function OnboardingUsageCard({
  summary,
  loading,
  refreshing,
  error,
  lastRefreshedAt,
  onRefresh,
}: OnboardingUsageCardProps) {
  const steps = normalizeOnboardingSteps(summary?.onboarding);
  const usage: OrganizationUsageSummary['usage'] | null = summary?.usage ?? null;
  const onboarding = summary?.onboarding ?? null;
  const completedCount = completedStepCount(steps);
  const totalCount = steps.length;
  const lastRefreshed = refreshing
    ? 'Refreshing...'
    : formatLastRefreshed(lastRefreshedAt);
  const onboardingUnavailable =
    onboarding?.available === false || Boolean(onboarding?.error);
  const allStepsIncomplete = steps.every((step) => !step.completed);

  return (
    <Card className="info-section onboarding-usage-card">
      <div className="section-header-inline onboarding-usage-header">
        <div className="onboarding-usage-title">
          <h3>Onboarding & Usage</h3>
          <Badge variant="outline" className="onboarding-usage-progress">
            {completedCount}/{totalCount} complete
          </Badge>
        </div>
        <div className="onboarding-usage-actions">
          <span className="onboarding-usage-refresh-state" aria-live="polite">
            {lastRefreshed ? `Last refreshed ${lastRefreshed}` : 'Not refreshed'}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading || refreshing}
            aria-label="Refresh onboarding and usage"
          >
            <RefreshCw className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>
      </div>

      {loading && !summary ? (
        <div className="onboarding-usage-loading">
          <Spinner size={24} label="Loading onboarding and usage..." />
        </div>
      ) : (
        <>
          {error && <InlineCardError message={error} />}
          {summary ? (
            <>
              <div className="usage-metric-grid">
                <div className="usage-metric">
                  <span className="usage-metric-label">Token usage</span>
                  <span className="usage-metric-value">
                    {formatUsageMetric(usage?.totalTokensUsed, 'Not used')}
                  </span>
                </div>
                <div className="usage-metric">
                  <span className="usage-metric-label">Days idle</span>
                  <span className="usage-metric-value">
                    {formatUsageMetric(usage?.daysIdle, 'No activity')}
                  </span>
                </div>
                <div className="usage-metric">
                  <span className="usage-metric-label">Core chats</span>
                  <span className="usage-metric-value">
                    {formatUsageMetric(usage?.coreChats, '0')}
                  </span>
                </div>
                <div className="usage-metric">
                  <span className="usage-metric-label">Studio projects</span>
                  <span className="usage-metric-value">
                    {formatUsageMetric(usage?.studioProjects, 'Unavailable')}
                  </span>
                </div>
              </div>

              <div className="onboarding-checklist-section">
                {onboardingUnavailable && (
                  <InlineCardError
                    message={
                      onboarding?.error ||
                      'Onboarding signals are unavailable right now.'
                    }
                  />
                )}
                {allStepsIncomplete && !onboardingUnavailable && (
                  <p className="onboarding-empty-state">No onboarding activity yet.</p>
                )}
                <ol className="onboarding-step-list" aria-label="Onboarding checklist">
                  {steps.map((step) => {
                    const timestamp =
                      step.completed
                        ? formatTimestamp(step.completedAt) ??
                          (formatTimestamp(summary.generatedAt)
                            ? `Observed ${formatTimestamp(summary.generatedAt)}`
                            : 'Completed')
                        : null;
                    return (
                      <li
                        key={step.key}
                        className={`onboarding-step ${step.completed ? 'completed' : ''}`}
                      >
                        <span
                          className="onboarding-step-ring"
                          aria-label={
                            step.completed
                              ? `${step.label} complete`
                              : `${step.label} not complete`
                          }
                        >
                          {step.completed ? <Check className="h-4 w-4" /> : step.position}
                        </span>
                        <span className="onboarding-step-main">
                          <span className="onboarding-step-label">{step.label}</span>
                          {step.count !== null && (
                            <Badge variant="outline" className="onboarding-step-count">
                              {NUMBER_FORMATTER.format(step.count)}
                            </Badge>
                          )}
                        </span>
                        {timestamp && (
                          <span className="onboarding-step-time">{timestamp}</span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            </>
          ) : (
            !error && (
              <InlineCardError message="Onboarding and usage are unavailable." />
            )
          )}
        </>
      )}
    </Card>
  );
}
